import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Slide,
  Grid,
  DialogActions,
  Typography,
  Drawer,
  Box,
  Stack,
  IconButton,
  Paper,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloudCircleIcon from '@mui/icons-material/CloudCircle';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { notification } from 'antd';
import CloseIcon from '@mui/icons-material/Close';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);
const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.txt', '.csv', '.docx', '.xlsx'];
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/plain',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export default function DialogUpload(props) {
  const { open, handleClose, handleFileChange, files, onRemoveFile } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleValidatedChange = (event) => {
    const selections = Array.from(event.target.files || []);
    if (!selections.length) {
      return;
    }
    const validFiles = [];
    const rejectedReasons = [];

    selections.forEach((file) => {
      const extension = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
      if (!ALLOWED_EXTENSIONS.includes(extension) || !ALLOWED_MIME_TYPES.has(file.type)) {
        rejectedReasons.push(`${file.name} (${t('Uploadfiles.invalidType', { defaultValue: 'type not allowed' })})`);
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        rejectedReasons.push(`${file.name} (${t('Uploadfiles.tooBig', { defaultValue: 'exceeds 5MB' })})`);
        return;
      }
      validFiles.push(file);
    });

    if (rejectedReasons.length) {
      notification.error({
        message: t('Uploadfiles.invalidFiles', { defaultValue: 'Some files were rejected' }),
        description: rejectedReasons.join(', '),
      });
    }

    if (validFiles.length) {
      handleFileChange(validFiles);
    }
  };

  const body = (
    <>
      <DialogTitle>
        <Grid container direction="row">
          <Grid item xs={1} alignSelf={'center'}>
            <CloudCircleIcon fontSize="large" style={{ color: '#A9ACB4' }} />
          </Grid>
          <Grid item xs container direction={'row'}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('Uploadfiles.1')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography style={{ color: '#A9ACB4' }}>{t('Uploadfiles.2')}</Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ borderBottom: '3px solid #CBD0DC' }} />
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid textAlign={'center'} container direction={'row'} className="rounded-2xl border-2 border-dotted border-slate-300 p-5">
          <Grid item xs={12}>
            <CloudDoneIcon />
          </Grid>
          <Grid item xs={12}>
            <Typography>{t('Uploadfiles.3')}</Typography>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 10 }}>
            <Stack spacing={1} alignItems="center">
              <Button
                variant="outlined"
                component="label"
                className="w-48"
                sx={{ borderStyle: 'dashed', borderColor: '#CBD0DC', color: '#0F172A' }}
              >
                {t('Uploadfiles.chooseButton', { defaultValue: 'Choose Files' })}
                <input
                  hidden
                  type="file"
                  accept={ALLOWED_EXTENSIONS.map((ext) => `*${ext}`).join(',')}
                  multiple
                  onChange={handleValidatedChange}
                />
              </Button>
              <Typography variant="caption" className="text-gray-500">
                {t('Uploadfiles.constraints', {
                  defaultValue: 'Only PDF, PNG, JPG, TXT, CSV, DOCX, XLSX up to 5MB each.',
                })}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        <Grid container direction="column" spacing={1.5} sx={{ mt: 2 }}>
          {files.map((item, index) => {
            const name = item.name || item.nama_file || item.filename || `File-${index + 1}`;
            const sizeLabel =
              typeof item.size === 'number' && item.size > 0 ? `${(item.size / 1048576).toFixed(2)} MB` : null;
            return (
              <Grid key={item.id || item.key || name} item xs={12}>
                <Paper className="flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 shadow-none">
                  <Box className="flex items-center justify-center rounded-full bg-white p-1">
                    <CloudCircleIcon className="text-slate-400" fontSize="small" />
                  </Box>
                  <Box className="flex-1 min-w-0 text-left">
                    <Typography variant="body2" className="truncate font-semibold text-slate-800">
                      {name}
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      {[sizeLabel, t('Uploadfiles.5')].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {typeof onRemoveFile === 'function' && (
                    <IconButton color="error" onClick={() => onRemoveFile(index)} aria-label="remove file" size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions className="px-6 pb-6">
        <Button variant="outlined" onClick={handleClose} style={{ color: 'red' }}>
          {t('Cancel.1')}
        </Button>
        <Button style={{ color: 'white', backgroundColor: '#1eb3d9' }} onClick={handleClose}>
          {t('Uploadfiles.6')}
        </Button>
      </DialogActions>
    </>
  );

  if (isMobile) {
    return (
      <Drawer anchor="bottom" open={open} onClose={handleClose} PaperProps={{ className: 'rounded-t-3xl', sx: { maxHeight: '90vh' } }}>
        <Box className="flex flex-col gap-2">{body}</Box>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
      fullWidth
      maxWidth={'md'}
      aria-describedby="alert-dialog-slide-description"
    >
      {body}
    </Dialog>
  );
}

DialogUpload.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleFileChange: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      nama_file: PropTypes.string,
    })
  ).isRequired,
  onRemoveFile: PropTypes.func,
};
